import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Minus, ShoppingCart, Clock, Check } from 'lucide-react';
import './Rentals.css';

// Rental images from mobile app
const RENTAL_IMAGES = {
  'classic-booth': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/uktllpe8_shopping.webp',
  'luxe-booth': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/0rc2keu8_download.webp',
  '360-booth': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/hfjhx5z1_images%20%284%29.jpeg',
  'cold-sparks': 'https://customer-assets.emergentagent.com/job_044b4dba-3d76-4c2b-a188-59d0d1c8970e/artifacts/ep1wpvom_Cold_Sparklers_Machine__48123.1743518077.500.750.png',
  'low-fog': 'https://customer-assets.emergentagent.com/job_044b4dba-3d76-4c2b-a188-59d0d1c8970e/artifacts/dbrupu71_download%20%281%29.avif',
  'foam-machine': 'https://customer-assets.emergentagent.com/job_044b4dba-3d76-4c2b-a188-59d0d1c8970e/artifacts/npel20sx_images%20%282%29.jpeg',
  'snow-machine': 'https://customer-assets.emergentagent.com/job_044b4dba-3d76-4c2b-a188-59d0d1c8970e/artifacts/kymvqieo_images%20%283%29.jpeg',
  'laser': 'https://customer-assets.emergentagent.com/job_044b4dba-3d76-4c2b-a188-59d0d1c8970e/artifacts/0xf394lb_download.avif',
  'led-glow': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/xoillvkw_Screenshot_20260216_235545_TikTok.jpg',
  'tables': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/8kniqy3i_Screenshot_20260217_235751_Temu.jpg',
  'cotton-candy': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/acaszye2_Screenshot_20260217_235711_Temu.jpg',
  'connect4': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/9rta24jc_Screenshot_20260217_235724_Temu.jpg',
  'jenga': 'https://customer-assets.emergentagent.com/job_044b4dba-3d76-4c2b-a188-59d0d1c8970e/artifacts/1iz7zlro_Screenshot_20260218_000152_Walmart.jpg',
  'axe-throwing': 'https://customer-assets.emergentagent.com/job_snapbooth-67/artifacts/caikh4f9_Screenshot_20260218_000046_TikTok.jpg',
  'projector': 'https://images.unsplash.com/photo-1721733258290-cac1a9204564?w=400',
  'speaker': 'https://images.unsplash.com/photo-1545454675-3531b543be5d?w=400',
  'popcorn': 'https://images.unsplash.com/photo-1585647347483-22b66260dfff?w=400',
  'backdrop': 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400',
};

// Rental data matching mobile app
const RENTALS_DATA = {
  photoBooths: {
    title: '📸 Photo Booths',
    items: [
      { id: 'classic-booth', name: 'Classic Photo Booth', pricePerHour: 150, minHours: 2, description: 'Traditional photo booth with instant prints', includes: ['Unlimited sessions', '2x6 prints', 'Props', 'Attendant'], image: RENTAL_IMAGES['classic-booth'] },
      { id: 'luxe-booth', name: 'Luxe Photo Booth', pricePerHour: 200, minHours: 2, description: 'Premium Photo Booth Experience', includes: ['Premium quality', '2x6 or 4x6 prints', 'Custom template', 'Digital sharing'], image: RENTAL_IMAGES['luxe-booth'] },
      { id: '360-booth', name: '360° Spin Booth', pricePerHour: 185, minHours: 2, description: 'Immersive 360-degree video capture', includes: ['Unlimited 360 videos', 'Instant sharing', 'LED lighting', 'Operator'], image: RENTAL_IMAGES['360-booth'] },
    ],
  },
  specialEffects: {
    title: '✨ Special Effects',
    items: [
      { id: 'cold-sparks', name: 'Cold Spark Package', pricePerHour: 150, minHours: 2, description: 'Indoor-safe spark fountains (2 machines)', includes: ['2 machines', 'Controlled runtime', 'On-site operator'], image: RENTAL_IMAGES['cold-sparks'] },
      { id: 'low-fog', name: 'Low-Lying Fog', pricePerHour: 275, minHours: 1, description: 'Perfect for first dances & grand entrances', includes: ['1-2 moments', 'Setup & operator'], image: RENTAL_IMAGES['low-fog'] },
      { id: 'foam-machine', name: 'Foam Machine', pricePerHour: 200, minHours: 2, description: 'Fun foam party experience', includes: ['Foam fluid', 'Operator', 'Setup/cleanup'], image: RENTAL_IMAGES['foam-machine'] },
      { id: 'snow-machine', name: 'Snow Machine', pricePerHour: 175, minHours: 2, description: 'Artificial snow effect', includes: ['Snow fluid', 'Operator'], image: RENTAL_IMAGES['snow-machine'] },
      { id: 'laser', name: 'Laser Light Show', pricePerHour: 200, minHours: 2, description: 'Professional laser light experience', includes: ['Multi-color lasers', 'DMX controlled', 'Operator'], image: RENTAL_IMAGES['laser'] },
    ],
  },
  audioVisual: {
    title: '🎬 Audio & Visual',
    items: [
      { id: 'projector-100', name: 'Indoor Projection (100")', pricePerHour: 200, minHours: 2, description: 'HD projector with 100" screen', includes: ['HD Projector', '100" Screen', 'Sound system'], image: RENTAL_IMAGES['projector'] },
      { id: 'projector-outdoor', name: 'Outdoor Movie (16ft)', pricePerHour: 250, minHours: 2, description: 'Large outdoor movie screen', includes: ['16ft inflatable screen', 'HD projector', 'PA speakers'], image: RENTAL_IMAGES['projector'] },
      { id: 'speaker-basic', name: 'Bluetooth Speaker', pricePerHour: 50, minHours: 2, description: 'Portable sound solution', includes: ['JBL Partybox', 'Mic input'], image: RENTAL_IMAGES['speaker'] },
      { id: 'speaker-pro', name: 'PA Speaker System', pricePerHour: 100, minHours: 2, description: 'Professional sound system', includes: ['2 speakers', 'Subwoofer', 'Mixer'], image: RENTAL_IMAGES['speaker'] },
    ],
  },
  lighting: {
    title: '💡 Lighting',
    items: [
      { id: 'led-glow', name: 'LED Glow Sticks (50pk)', pricePerHour: 75, minHours: 1, description: 'Party glow sticks for guests', includes: ['50 glow sticks', 'Assorted colors'], image: RENTAL_IMAGES['led-glow'] },
      { id: 'led-uplighting', name: 'LED Uplighting (8 units)', pricePerHour: 150, minHours: 2, description: 'Color wash for venues', includes: ['8 LED uplights', 'DMX controller', 'Setup'], image: RENTAL_IMAGES['led-glow'] },
      { id: 'string-lights', name: 'String Lights (100ft)', pricePerHour: 100, minHours: 2, description: 'Warm ambiance lighting', includes: ['100ft string lights', 'Installation'], image: RENTAL_IMAGES['led-glow'] },
    ],
  },
  tablesFurniture: {
    title: '🪑 Tables & Furniture',
    items: [
      { id: '6ft-table', name: '6ft Folding Table', pricePerHour: 15, minHours: 2, description: 'Standard rectangular table', includes: ['Table', 'Delivery'], image: RENTAL_IMAGES['tables'] },
      { id: '8ft-table', name: '8ft Folding Table', pricePerHour: 20, minHours: 2, description: 'Large rectangular table', includes: ['Table', 'Delivery'], image: RENTAL_IMAGES['tables'] },
      { id: 'round-table', name: '60" Round Table', pricePerHour: 25, minHours: 2, description: 'Seats 8-10 guests', includes: ['Table', 'Delivery'], image: RENTAL_IMAGES['tables'] },
      { id: 'cocktail-table', name: 'Cocktail Table', pricePerHour: 20, minHours: 2, description: 'High-top standing table', includes: ['Table', 'Spandex cover'], image: RENTAL_IMAGES['tables'] },
      { id: 'chairs', name: 'Folding Chairs (10)', pricePerHour: 30, minHours: 2, description: 'Set of 10 folding chairs', includes: ['10 chairs', 'Delivery'], image: RENTAL_IMAGES['tables'] },
    ],
  },
  concessions: {
    title: '🍿 Concessions',
    items: [
      { id: 'popcorn', name: 'Popcorn Machine', pricePerHour: 100, minHours: 2, description: 'Fresh popcorn for guests', includes: ['Machine', 'Supplies for 50', 'Operator'], image: RENTAL_IMAGES['popcorn'] },
      { id: 'cotton-candy', name: 'Cotton Candy Machine', pricePerHour: 100, minHours: 2, description: 'Fresh cotton candy', includes: ['Machine', 'Supplies for 50', 'Operator'], image: RENTAL_IMAGES['cotton-candy'] },
      { id: 'snow-cone', name: 'Snow Cone Machine', pricePerHour: 100, minHours: 2, description: 'Refreshing snow cones', includes: ['Machine', 'Supplies for 50', 'Operator'], image: RENTAL_IMAGES['popcorn'] },
    ],
  },
  games: {
    title: '🎮 Games',
    items: [
      { id: 'giant-connect4', name: 'Giant Connect 4', pricePerHour: 75, minHours: 2, description: 'Life-size Connect 4 game', includes: ['Game set', 'Delivery'], image: RENTAL_IMAGES['connect4'] },
      { id: 'giant-jenga', name: 'Giant Jenga', pricePerHour: 75, minHours: 2, description: 'Oversized Jenga tower', includes: ['54 blocks', 'Carrying case'], image: RENTAL_IMAGES['jenga'] },
      { id: 'cornhole', name: 'Cornhole Set', pricePerHour: 50, minHours: 2, description: 'Classic bean bag toss', includes: ['2 boards', '8 bags'], image: RENTAL_IMAGES['connect4'] },
      { id: 'axe-throwing', name: 'Inflatable Axe Throwing', pricePerHour: 150, minHours: 2, description: 'Safe inflatable dartboard', includes: ['Inflatable target', 'Soft axes', 'Setup'], image: RENTAL_IMAGES['axe-throwing'] },
    ],
  },
  backdrops: {
    title: '🎨 Backdrops',
    items: [
      { id: 'shimmer-wall', name: 'Shimmer Wall', pricePerHour: 150, minHours: 2, description: 'Sequin shimmer backdrop', includes: ['8x8 backdrop', 'Frame', 'Setup'], image: RENTAL_IMAGES['backdrop'] },
      { id: 'floral-backdrop', name: 'Floral Backdrop', pricePerHour: 200, minHours: 2, description: 'Artificial floral wall', includes: ['8x8 backdrop', 'Setup'], image: RENTAL_IMAGES['backdrop'] },
      { id: 'balloon-arch', name: 'Balloon Arch', pricePerHour: 175, minHours: 2, description: 'Custom balloon arch', includes: ['Full arch', 'Installation'], image: RENTAL_IMAGES['backdrop'] },
      { id: 'red-carpet', name: 'Red Carpet Setup', pricePerHour: 100, minHours: 2, description: 'VIP entrance experience', includes: ['25ft carpet', 'Stanchions', 'Setup'], image: RENTAL_IMAGES['backdrop'] },
    ],
  },
};

export default function Rentals() {
  const [cart, setCart] = useState({});
  const [activeCategory, setActiveCategory] = useState('photoBooths');

  const addToCart = (item) => {
    setCart(prev => ({
      ...prev,
      [item.id]: {
        ...item,
        hours: prev[item.id]?.hours || item.minHours,
        quantity: (prev[item.id]?.quantity || 0) + 1,
      }
    }));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[itemId]?.quantity > 1) {
        newCart[itemId].quantity -= 1;
      } else {
        delete newCart[itemId];
      }
      return newCart;
    });
  };

  const updateHours = (itemId, hours) => {
    setCart(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], hours: Math.max(prev[itemId].minHours, hours) }
    }));
  };

  const getCartTotal = () => {
    return Object.values(cart).reduce((total, item) => {
      return total + (item.pricePerHour * item.hours * item.quantity);
    }, 0);
  };

  const cartItemCount = Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="rentals-page">
      <section className="rentals-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">Individual Rentals</span>
            <h1 className="page-title">Build Your Own Package</h1>
            <p className="page-description">
              Mix and match our rental items to create your perfect event experience.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section rentals-section">
        <div className="container">
          <div className="rentals-layout">
            {/* Category Sidebar */}
            <aside className="rentals-sidebar">
              <nav className="category-nav">
                {Object.entries(RENTALS_DATA).map(([key, category]) => (
                  <button
                    key={key}
                    className={`category-btn ${activeCategory === key ? 'active' : ''}`}
                    onClick={() => setActiveCategory(key)}
                  >
                    {category.title}
                  </button>
                ))}
              </nav>

              {/* Cart Summary */}
              {cartItemCount > 0 && (
                <div className="cart-summary">
                  <div className="cart-header">
                    <ShoppingCart size={20} />
                    <span>Your Cart ({cartItemCount})</span>
                  </div>
                  <div className="cart-items">
                    {Object.values(cart).map(item => (
                      <div key={item.id} className="cart-item">
                        <span className="cart-item-name">{item.name}</span>
                        <span className="cart-item-qty">x{item.quantity}</span>
                        <span className="cart-item-price">${item.pricePerHour * item.hours * item.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <div className="cart-total">
                    <span>Total</span>
                    <span>${getCartTotal()}</span>
                  </div>
                  <Link to="/book" className="btn btn-primary w-full">
                    Proceed to Book
                  </Link>
                </div>
              )}
            </aside>

            {/* Items Grid */}
            <div className="rentals-content">
              <h2 className="category-title">{RENTALS_DATA[activeCategory].title}</h2>
              <div className="rentals-grid">
                {RENTALS_DATA[activeCategory].items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="rental-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <div className="rental-image">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="rental-info">
                      <h3 className="rental-name">{item.name}</h3>
                      <p className="rental-description">{item.description}</p>
                      <div className="rental-price">
                        <span className="price">${item.pricePerHour}</span>
                        <span className="per-hour">/hour</span>
                      </div>
                      <div className="rental-meta">
                        <Clock size={14} />
                        <span>{item.minHours}hr minimum</span>
                      </div>
                      <ul className="rental-includes">
                        {item.includes.map((inc, i) => (
                          <li key={i}>
                            <Check size={12} />
                            <span>{inc}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="rental-actions">
                        {cart[item.id] ? (
                          <div className="quantity-control">
                            <button onClick={() => removeFromCart(item.id)}><Minus size={16} /></button>
                            <span>{cart[item.id].quantity}</span>
                            <button onClick={() => addToCart(item)}><Plus size={16} /></button>
                          </div>
                        ) : (
                          <button className="btn btn-primary" onClick={() => addToCart(item)}>
                            Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
